import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';

// Helper function to decode JWT token payload
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const getCognitoStoragePrefix = () => {
  const client_id = import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID;
  if (!client_id) {
    return null;
  }
  return `CognitoIdentityServiceProvider.${client_id}`;
};

const getLocalCognitoKeys = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }
  const prefix = getCognitoStoragePrefix();
  if (!prefix) {
    return [];
  }
  const matching_keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      matching_keys.push(key);
    }
  }
  return matching_keys;
};

const clearLocalCognitoTokens = () => {
  getLocalCognitoKeys().forEach((key) => localStorage.removeItem(key));
};

const AUTH_RECOVERY_SKIP_PATHS = ['/', '/logout', '/auth/callback'];
const REFRESH_WINDOW_MS = 5 * 60 * 1000;
const REFRESH_COOLDOWN_MS = 60 * 1000;

let auth_failed = false;
let refresh_promise = null;
let recovery_started = false;
let last_refresh_at = 0;

export const isUsableSession = (session) => {
  return Boolean(
    session &&
      session.credentials &&
      session.credentials.idToken &&
      session.expires &&
      session.expires * 1000 > Date.now(),
  );
};

export const isAuthFailed = () => auth_failed;

export const clearAuthFailure = () => {
  auth_failed = false;
  recovery_started = false;
};

export const createAuthError = () => {
  const error = new Error('Authentication required');
  error.isAuthError = true;
  return error;
};

const markAuthFailed = (reason) => {
  if (auth_failed) {
    return;
  }
  auth_failed = true;
  console.warn('Refresh token is unusable, sign-in required:', reason);
};

const isRefreshTokenDead = (error) => {
  const message = (error?.message || error?.toString() || '').toLowerCase();
  const name = error?.name || '';
  const code = error?.code || '';
  return (
    message.includes('refresh token has expired') ||
    message.includes('refresh token expired') ||
    message.includes('invalid refresh token') ||
    message.includes('refresh token has been revoked') ||
    message.includes('invalid_grant') ||
    ((name === 'NotAuthorizedException' || code === 'NotAuthorizedException') &&
      message.includes('refresh'))
  );
};

const hasStoredRefreshToken = () => {
  return getLocalCognitoKeys().some((key) => key.endsWith('.refreshToken'));
};

const shouldRecoverAuth = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return !AUTH_RECOVERY_SKIP_PATHS.includes(window.location.pathname);
};

const readSession = async (force_refresh) => {
  const session = await fetchAuthSession({ forceRefresh: force_refresh });
  if (!session || !session.tokens || !session.tokens.idToken) {
    return null;
  }

  const id_token = session.tokens.idToken;
  const access_token = session.tokens.accessToken;
  const id_token_string =
    typeof id_token === 'string' ? id_token : id_token.toString();
  const access_token_string = access_token
    ? typeof access_token === 'string'
      ? access_token
      : access_token.toString()
    : null;
  const id_token_payload = decodeJWT(id_token_string);
  if (!id_token_payload) {
    return null;
  }

  const parsed_session = {
    expires: id_token_payload.exp,
    credentials: {
      accessToken: access_token_string,
      idToken: id_token_string,
      refreshToken: null, // refreshToken is managed internally in v6
    },
    user: {
      customer_uuid: id_token_payload.sub,
      userName:
        id_token_payload['cognito:username'] || id_token_payload.username,
      email: id_token_payload.email,
      groups: id_token_payload['cognito:groups'] || [],
      given_name: id_token_payload.given_name,
      family_name: id_token_payload.family_name,
      name: id_token_payload.name,
      locale: id_token_payload.locale,
    },
  };

  if (!isUsableSession(parsed_session)) {
    return null;
  }
  return parsed_session;
};

// Updated extractSession to work with v6 token structure.
// Returns null unless the id token exists and is still unexpired.
export const extractSession = async (forceRefresh = false) => {
  if (auth_failed) {
    return null;
  }
  try {
    return await readSession(forceRefresh);
  } catch (error) {
    console.error('Error extracting session:', {
      message: error?.message,
      name: error?.name,
      code: error?.code,
      error: error,
    });
    return null;
  }
};

const finishRefreshFailure = (error, cached_session) => {
  if (isRefreshTokenDead(error)) {
    markAuthFailed(error?.message || 'refresh token rejected');
    return null;
  }
  if (isUsableSession(cached_session)) {
    if (error) {
      console.warn('Token refresh failed, keeping current session:', error);
      last_refresh_at = Date.now();
    }
    return cached_session;
  }
  // The refresh token is still stored. A network or transient Cognito
  // failure must not clear it or send the user through sign-in.
  if (!hasStoredRefreshToken()) {
    markAuthFailed(error?.message || 'no refresh token');
  }
  return null;
};

const refreshSession = (bypass_cooldown = false) => {
  if (auth_failed) {
    return Promise.resolve(null);
  }
  if (refresh_promise) {
    return refresh_promise;
  }
  let current_refresh;
  current_refresh = (async () => {
    let cached_session = null;
    let cached_error = null;
    try {
      cached_session = await readSession(false);
    } catch (error) {
      cached_error = error;
      cached_session = null;
    }

    if (isRefreshTokenDead(cached_error)) {
      return finishRefreshFailure(cached_error, null);
    }

    const refreshed_recently =
      Date.now() - last_refresh_at < REFRESH_COOLDOWN_MS;
    const ms_left = isUsableSession(cached_session)
      ? cached_session.expires * 1000 - Date.now()
      : 0;
    if (
      isUsableSession(cached_session) &&
      !bypass_cooldown &&
      (ms_left > REFRESH_WINDOW_MS || refreshed_recently)
    ) {
      return cached_session;
    }

    try {
      const refreshed_session = await readSession(true);
      if (isUsableSession(refreshed_session)) {
        last_refresh_at = Date.now();
        return refreshed_session;
      }
    } catch (error) {
      return finishRefreshFailure(error, cached_session);
    }

    return finishRefreshFailure(cached_error, cached_session);
  })().finally(() => {
    if (refresh_promise === current_refresh) {
      refresh_promise = null;
    }
  });
  refresh_promise = current_refresh;
  return current_refresh;
};

// One in-flight refresh shared by Layout, API calls, and the websocket.
// A valid id token is returned as-is. It is refreshed only when it is
// expired or inside the 5-minute window, using the stored refresh token.
export const loadSession = async (force_refresh = false, options = {}) => {
  if (auth_failed) {
    return null;
  }
  if (refresh_promise) {
    return refresh_promise;
  }
  const bypass_cooldown = Boolean(options.bypass_cooldown);
  const session = await extractSession(false);
  if (!force_refresh && isUsableSession(session)) {
    return session;
  }
  return refreshSession(bypass_cooldown);
};

export const recoverAuth = async () => {
  if (!shouldRecoverAuth() || recovery_started) {
    return;
  }
  recovery_started = true;
  markAuthFailed('recovering session');

  localStorage.setItem('redirect_path', window.location.pathname);
  localStorage.setItem('redirect_search', window.location.search);
  clearLocalCognitoTokens();

  try {
    await signInWithRedirect();
  } catch (error) {
    if (error?.name !== 'UserAlreadyAuthenticatedException') {
      console.error('Error recovering auth:', error);
      return;
    }
    clearLocalCognitoTokens();
    try {
      await signInWithRedirect();
    } catch (retry_error) {
      console.error('Error restarting sign in:', retry_error);
    }
  }
};

const restoreOrClearStaleSession = async () => {
  if (getLocalCognitoKeys().length === 0) {
    return null;
  }
  const session = await loadSession(true);
  if (isUsableSession(session)) {
    return session;
  }
  clearLocalCognitoTokens();
  return null;
};

// Use signInWithRedirect for v6 OAuth flow
export const initiateSignIn = async () => {
  clearAuthFailure();
  const existing_session = await restoreOrClearStaleSession();
  if (existing_session) {
    return existing_session;
  }

  try {
    await signInWithRedirect();
    return null;
  } catch (error) {
    if (error?.name === 'UserAlreadyAuthenticatedException') {
      clearLocalCognitoTokens();
      const restored_session = await loadSession(true);
      if (isUsableSession(restored_session)) {
        return restored_session;
      }
      clearLocalCognitoTokens();
      await signInWithRedirect();
      return null;
    }
    console.error('Error initiating sign in:', error);
    throw error;
  }
};

// Legacy function for backward compatibility - now uses signInWithRedirect
export const getCognitoSignInUrl = () => {
  // For v6, we should use signInWithRedirect instead of manual URL
  // This is kept for backward compatibility but should be replaced
  const signinUrl = `https://${import.meta.env.VITE_AUTH_DOMAIN}/login?response_type=code&client_id=${import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID}&redirect_uri=${import.meta.env.VITE_REDIRECT_SIGNIN}`;
  return signinUrl;
};

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID,
      region: import.meta.env.VITE_REGION,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_AUTH_DOMAIN,
          scopes: ['email', 'profile', 'openid'],
          redirectSignIn: [import.meta.env.VITE_REDIRECT_SIGNIN],
          redirectSignOut: [import.meta.env.VITE_REDIRECT_SIGNOUT],
          responseType: 'code',
        },
      },
    },
  },
});
